import { api } from './api';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export type ConsentFormType = 'telehealth' | 'hipaa' | 'financial' | 'treatment' | 'custom';

export interface ConsentTemplate {
  id: string;
  name: string;
  description?: string;
  content: string;
  form_type: ConsentFormType;
  version: number;
  is_default: boolean;
  is_active: boolean;
  is_deleted: boolean;
  is_required_for_booking: boolean;
  is_required_for_telehealth: boolean;
  signature_required: boolean;
  expires_after_days?: number;
  created_at: string;
  updated_at: string;
  clinic?: {
    id: string;
    name: string;
  };
}

export interface CreateConsentTemplateRequest {
  name: string;
  description?: string;
  content: string;
  form_type: ConsentFormType;
  is_required_for_booking?: boolean;
  is_required_for_telehealth?: boolean;
  signature_required?: boolean;
  expires_after_days?: number;
}

export interface UpdateConsentTemplateRequest {
  name?: string;
  description?: string;
  content?: string;
  form_type?: ConsentFormType;
  is_active?: boolean;
  is_required_for_booking?: boolean;
  is_required_for_telehealth?: boolean;
  signature_required?: boolean;
  expires_after_days?: number;
}

// Response types
export interface ConsentTemplatesResponse {
  success: boolean;
  data: ConsentTemplate[];
  message?: string;
}

export interface ConsentTemplateResponse {
  success: boolean;
  data: ConsentTemplate;
  message?: string;
}

export interface DeleteConsentTemplateResponse {
  success: boolean;
  message?: string;
}

export interface GetTemplatesParams {
  includeInactive?: boolean;
}

// Patient consent types
export interface PatientConsentSignature {
  id: string;
  patient_id: string;
  consent_form_template_id: string;
  clinic_id: string;
  template_version: number;
  signed_content: string;
  signature_type: 'drawn' | 'typed' | 'checkbox';
  signature_data?: {
    type: 'drawn' | 'typed';
    data: string;
    timestamp: string;
  };
  signed_at: string;
  expires_at?: string;
  is_valid: boolean;
  revoked_at?: string;
  revocation_reason?: string;
  consentTemplate?: ConsentTemplate;
}

export interface PatientConsentsResponse {
  success: boolean;
  data: PatientConsentSignature[];
  message?: string;
}

export interface ConsentCheckResult {
  hasAll: boolean;
  missing: Array<{
    id: string;
    name: string;
    form_type: ConsentFormType;
  }>;
}

export interface ConsentCheckResponse {
  success: boolean;
  data: ConsentCheckResult;
  message?: string;
}

export interface RecordConsentRequest {
  consent_form_template_id: string;
  signature_type: 'drawn' | 'typed' | 'checkbox';
  signature_data?: {
    type: 'drawn' | 'typed';
    data: string;
    timestamp: string;
  };
}

export interface RecordConsentResponse {
  success: boolean;
  data: PatientConsentSignature;
  message?: string;
}

export interface RevokeConsentResponse {
  success: boolean;
  message?: string;
}

export interface ConsentDashboardData {
  templates: Array<{
    id: string;
    name: string;
    form_type: ConsentFormType;
    is_required_for_booking: boolean;
    is_required_for_telehealth: boolean;
    signature_count: number;
  }>;
  recent_signatures: PatientConsentSignature[];
}

export interface ConsentDashboardResponse {
  success: boolean;
  data: ConsentDashboardData;
  message?: string;
}

// ============================================================================
// CONSENT SERVICE
// ============================================================================

export const consentService = {
  /**
   * Get all consent templates for a clinic
   */
  getTemplates: (
    clinicId: string,
    params?: GetTemplatesParams
  ): Promise<ConsentTemplatesResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.includeInactive) {
      queryParams.append('includeInactive', 'true');
    }
    const queryString = queryParams.toString();
    return api.get(
      `/clinic/${clinicId}/consents/templates${queryString ? `?${queryString}` : ''}`
    );
  },

  /**
   * Get a specific consent template by ID
   */
  getTemplate: (
    clinicId: string,
    templateId: string
  ): Promise<ConsentTemplateResponse> => {
    return api.get(`/clinic/${clinicId}/consents/templates/${templateId}`);
  },

  /**
   * Create a new consent template
   */
  createTemplate: (
    clinicId: string,
    data: CreateConsentTemplateRequest
  ): Promise<ConsentTemplateResponse> => {
    return api.post(`/clinic/${clinicId}/consents/templates`, data);
  },

  /**
   * Update an existing consent template
   */
  updateTemplate: (
    clinicId: string,
    templateId: string,
    data: UpdateConsentTemplateRequest
  ): Promise<ConsentTemplateResponse> => {
    return api.put(`/clinic/${clinicId}/consents/templates/${templateId}`, data);
  },

  /**
   * Delete a consent template
   */
  deleteTemplate: (
    clinicId: string,
    templateId: string
  ): Promise<DeleteConsentTemplateResponse> => {
    return api.delete(`/clinic/${clinicId}/consents/templates/${templateId}`);
  },

  /**
   * Toggle template active status
   */
  toggleTemplateActive: (
    clinicId: string,
    templateId: string,
    isActive: boolean
  ): Promise<ConsentTemplateResponse> => {
    return api.put(`/clinic/${clinicId}/consents/templates/${templateId}`, {
      is_active: isActive,
    });
  },

  /**
   * Update template booking requirement
   */
  toggleBookingRequirement: (
    clinicId: string,
    templateId: string,
    isRequired: boolean
  ): Promise<ConsentTemplateResponse> => {
    return api.put(`/clinic/${clinicId}/consents/templates/${templateId}`, {
      is_required_for_booking: isRequired,
    });
  },

  /**
   * Update template telehealth requirement
   */
  toggleTelehealthRequirement: (
    clinicId: string,
    templateId: string,
    isRequired: boolean
  ): Promise<ConsentTemplateResponse> => {
    return api.put(`/clinic/${clinicId}/consents/templates/${templateId}`, {
      is_required_for_telehealth: isRequired,
    });
  },

  /**
   * Toggle template default status
   */
  toggleDefaultStatus: (
    clinicId: string,
    templateId: string,
    isDefault: boolean
  ): Promise<ConsentTemplateResponse> => {
    return api.put(`/clinic/${clinicId}/consents/templates/${templateId}`, {
      is_default: isDefault,
    });
  },

  // ============================================================================
  // PATIENT CONSENT METHODS
  // ============================================================================

  /**
   * Get all consent signatures for a patient at a clinic
   */
  getPatientConsents: (
    clinicId: string,
    patientId: string,
    includeExpired = false
  ): Promise<PatientConsentsResponse> => {
    const params = includeExpired ? '?includeExpired=true' : '';
    return api.get(`/clinic/${clinicId}/patients/${patientId}/consents${params}`);
  },

  /**
   * Check if patient has all required consents
   */
  /**
   * Check patient consents (uses public endpoint - no auth required)
   */
  checkPatientConsents: (
    clinicId: string,
    patientId: string,
    purpose: 'booking' | 'telehealth' = 'booking'
  ): Promise<ConsentCheckResponse> => {
    return api.get(`/public/consents/check?clinic_id=${clinicId}&patient_id=${patientId}&purpose=${purpose}`);
  },

  /**
   * Record a consent signature for a patient (staff use)
   */
  recordConsentSignature: (
    clinicId: string,
    patientId: string,
    data: RecordConsentRequest
  ): Promise<RecordConsentResponse> => {
    return api.post(`/clinic/${clinicId}/patients/${patientId}/consents`, data);
  },

  /**
   * Revoke a patient's consent signature
   */
  revokeConsentSignature: (
    clinicId: string,
    patientId: string,
    signatureId: string,
    reason?: string
  ): Promise<RevokeConsentResponse> => {
    return api.post(`/clinic/${clinicId}/patients/${patientId}/consents/${signatureId}/revoke`, {
      reason,
    });
  },

  /**
   * Get consent dashboard data for a clinic
   */
  getConsentDashboard: (clinicId: string): Promise<ConsentDashboardResponse> => {
    return api.get(`/clinic/${clinicId}/consents/dashboard`);
  },

  // ============================================================================
  // PUBLIC CONSENT METHODS (for patient-facing flows)
  // ============================================================================

  /**
   * Get consent template for public signing (no auth required)
   */
  getPublicConsentTemplate: (templateId: string): Promise<ConsentTemplateResponse> => {
    return api.get(`/public/consents/template?templateId=${templateId}`);
  },

  /**
   * Submit a consent signature (public, patient-facing)
   */
  submitPublicConsentSignature: (data: {
    patient_id: string;
    clinic_id: string;
    consent_form_template_id: string;
    signature_type: 'drawn' | 'typed' | 'checkbox';
    signature_data?: {
      type: 'drawn' | 'typed';
      data: string;
      timestamp: string;
    };
  }): Promise<RecordConsentResponse> => {
    return api.post('/public/consents/sign', data);
  },
};

// ============================================================================
// STANDALONE FUNCTIONS (for direct import)
// ============================================================================

/**
 * Get all consent templates for a clinic
 */
export async function getConsentTemplates(
  clinicId: string,
  params?: GetTemplatesParams
): Promise<ConsentTemplatesResponse> {
  return consentService.getTemplates(clinicId, params);
}

/**
 * Get a specific consent template by ID
 */
export async function getConsentTemplate(
  clinicId: string,
  templateId: string
): Promise<ConsentTemplateResponse> {
  return consentService.getTemplate(clinicId, templateId);
}

/**
 * Create a new consent template
 */
export async function createConsentTemplate(
  clinicId: string,
  data: CreateConsentTemplateRequest
): Promise<ConsentTemplateResponse> {
  return consentService.createTemplate(clinicId, data);
}

/**
 * Update an existing consent template
 */
export async function updateConsentTemplate(
  clinicId: string,
  templateId: string,
  data: UpdateConsentTemplateRequest
): Promise<ConsentTemplateResponse> {
  return consentService.updateTemplate(clinicId, templateId, data);
}

/**
 * Delete a consent template
 */
export async function deleteConsentTemplate(
  clinicId: string,
  templateId: string
): Promise<DeleteConsentTemplateResponse> {
  return consentService.deleteTemplate(clinicId, templateId);
}

/**
 * Get all consent signatures for a patient
 */
export async function getPatientConsents(
  clinicId: string,
  patientId: string,
  includeExpired = false
): Promise<PatientConsentsResponse> {
  return consentService.getPatientConsents(clinicId, patientId, includeExpired);
}

/**
 * Check if patient has all required consents
 */
export async function checkPatientConsents(
  clinicId: string,
  patientId: string,
  purpose: 'booking' | 'telehealth' = 'booking'
): Promise<ConsentCheckResponse> {
  return consentService.checkPatientConsents(clinicId, patientId, purpose);
}

/**
 * Record a consent signature for a patient
 */
export async function recordConsentSignature(
  clinicId: string,
  patientId: string,
  data: RecordConsentRequest
): Promise<RecordConsentResponse> {
  return consentService.recordConsentSignature(clinicId, patientId, data);
}

/**
 * Revoke a patient's consent signature
 */
export async function revokeConsentSignature(
  clinicId: string,
  patientId: string,
  signatureId: string,
  reason?: string
): Promise<RevokeConsentResponse> {
  return consentService.revokeConsentSignature(clinicId, patientId, signatureId, reason);
}

/**
 * Get consent dashboard data
 */
export async function getConsentDashboard(
  clinicId: string
): Promise<ConsentDashboardResponse> {
  return consentService.getConsentDashboard(clinicId);
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get form type display label
 */
export function getFormTypeLabel(formType: ConsentFormType): string {
  const labels: Record<ConsentFormType, string> = {
    telehealth: 'Telehealth',
    hipaa: 'HIPAA',
    financial: 'Financial',
    treatment: 'Treatment',
    custom: 'Custom',
  };
  return labels[formType] || formType;
}

/**
 * Get form type color class for badges
 */
export function getFormTypeColorClass(formType: ConsentFormType): string {
  const colors: Record<ConsentFormType, string> = {
    telehealth: 'bg-blue-100 text-blue-800',
    hipaa: 'bg-purple-100 text-purple-800',
    financial: 'bg-green-100 text-green-800',
    treatment: 'bg-orange-100 text-orange-800',
    custom: 'bg-gray-100 text-gray-800',
  };
  return colors[formType] || colors.custom;
}

export default consentService;

